import type {TranslateConfig} from '~/commands/translate';
import type {CodeMode, VarsResolver} from '~/commands/translate/utils';
import type {YandexTranslationConfig} from '.';
import type {AxiosResponse} from 'axios';
import type {Logger} from '~/core/logger';
import type {TargetStat} from '../../report';

import {extname, join, resolve} from 'node:path';
import {asyncify, eachLimit} from 'async';
import axios, {AxiosError} from 'axios';

import {LogLevel} from '~/core/logger';

import {
    FileLoader,
    TranslateError,
    applyConditions,
    compose,
    extract,
    languageRepath,
    resolveSchemas,
} from '../../utils';
import {TranslateLogger} from '../../logger';
import {RunReport, createTargetStat, reportError, reportExtractWarning} from '../../report';

import {AuthError, Defer, LimitExceed, RequestError, bytes} from './utils';

const REQUESTS_LIMIT = 15;
const BYTES_LIMIT = 10000;
const RETRY_LIMIT = 3;

const onFatalError = () => {
    process.exit(1);
};

export class Provider {
    readonly logger: TranslateLogger;

    private report?: RunReport;

    private skippedFiles = 0;

    constructor(config: TranslateConfig) {
        this.logger = new TranslateLogger(config);
    }

    pipe(logger: Logger) {
        this.logger.pipe(logger);
    }

    async skip(skipped: [string, string][]) {
        this.skippedFiles = skipped.length;
        this.logger.skipped(skipped);
    }

    async translate(files: string[], config: TranslateConfig & YandexTranslationConfig) {
        const {
            input,
            output,
            auth,
            folder,
            source,
            target: targets,
            vars,
            code,
            dryRun,
            timeout,
        } = config;

        this.report = RunReport.start(config, files.length, this.skippedFiles);

        try {
            for (const target of targets) {
                const translatorParams = {
                    input,
                    output,
                    auth,
                    sourceLanguage: source.language,
                    targetLanguage: target.language,
                    // yandexCloudTranslateGlossaryPairs,
                    folderId: folder,
                    // The run resolves presets per file; a config without a
                    // run (tests, direct calls) falls back to the flat vars.
                    varsFor: config.varsFor ?? (() => vars),
                    code,
                    dryRun,
                    timeout,
                };

                const cache = new Map<string, Defer>();
                const stat = createTargetStat();
                const request = requester(translatorParams, cache, stat);
                const translate = translator(request, cache, this.logger, stat);
                const process = processor(translatorParams, translate);

                await eachLimit(
                    files,
                    REQUESTS_LIMIT,
                    asyncify(async (file: string) => {
                        try {
                            this.logger.translate(file);
                            const warnings = await process(file);
                            stat.filesTranslated++;
                            if (warnings.length) {
                                stat.filesPartial++;
                            }
                            for (const warning of warnings) {
                                this.logger.warn(file, warning);
                                this.report?.addError(
                                    reportExtractWarning(warning, {
                                        target: target.language,
                                        path: file,
                                    }),
                                );
                            }
                            if (!dryRun) {
                                this.logger.translated(file);
                            }
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        } catch (error: any) {
                            stat.filesFailed++;
                            this.report?.addError(
                                reportError(error, {target: target.language, path: file}),
                            );

                            if (error instanceof TranslateError) {
                                this.logger.error(file, `${error.message}`, error.code);

                                if (error.fatal) {
                                    this.report?.close(this.logger, 'failed');
                                    onFatalError();
                                }
                            } else {
                                this.logger.error(file, error.message);
                            }
                        }
                    }),
                );

                this.logger.stat(`bytes: ${stat.bytes} chunks: ${stat.requests}`);

                this.report.addTarget(target.language, stat);
            }

            this.report.close(this.logger);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            if (error instanceof TranslateError) {
                this.logger.topic(LogLevel.ERROR, error.code)(error.message);
            } else {
                this.logger.error(error);
            }

            this.report.addError(reportError(error));
            this.report.close(this.logger, 'failed');
            process.exit(1);
        }
    }
}

type TranslatorParams = {
    input: string;
    output: string;
    sourceLanguage: string;
    targetLanguage: string;
    varsFor: VarsResolver;
    code: CodeMode;
    // yandexCloudTranslateGlossaryPairs: YandexCloudTranslateGlossaryPair[];
};

type RequesterParams = {
    auth: string;
    folderId: string | undefined;
    sourceLanguage: string;
    targetLanguage: string;
    dryRun: boolean;
    timeout: number;
};

type Request = (texts: string[]) => () => Promise<void>;

type Translate = (path: string, texts: string[]) => Promise<string[]>;

type Cache = Map<string, Defer>;

type Translations = {
    translations: {
        text: string;
    }[];
};

function scheduler(limit: number, interval: number) {
    const scheduled: Defer<void>[] = [];

    let processing = 0;

    function idle() {
        const defer = new Defer<void>();

        scheduled.push(defer);

        return defer.promise;
    }

    async function queue() {
        processing++;
        await wait(interval);
        processing--;
        unqueue();
    }

    async function unqueue() {
        scheduled.shift()?.resolve();
    }

    return async function <R>(action: Function): Promise<R> {
        if (processing >= limit) {
            await idle();
        }

        queue();

        return action();
    };
}

function requester(params: RequesterParams, cache: Cache, stat: TargetStat): Request {
    const {auth, folderId, sourceLanguage, targetLanguage, dryRun, timeout} = params;
    const schedule = scheduler(REQUESTS_LIMIT, 1000);

    return function request(texts: string[]) {
        // The backoff loop may run the request action more than once -
        // resolve (and count) every unit at most once per chunk.
        const resolved = new Set<number>();
        const resolve = (text: string, index: number) => {
            if (resolved.has(index)) {
                return;
            }
            resolved.add(index);

            const defer = cache.get(texts[index]);
            if (defer) {
                stat.translatedUnits++;
                stat.translatedChars += text.length;
                defer.resolve(text);
            }
        };

        stat.bytes += bytes(texts);
        stat.requests++;

        return async function () {
            if (dryRun) {
                texts.forEach(resolve);
                return;
            }

            try {
                const {data} = await schedule<AxiosResponse<Translations>>(() =>
                    axios({
                        method: 'POST',
                        url: 'https://translate.api.cloud.yandex.net/translate/v2/translate',
                        timeout,
                        maxRedirects: 0,
                        headers: {
                            Authorization: auth,
                            'Content-Type': 'application/json',
                            'User-Agent': 'github.com/diplodoc-platform/cli',
                        },
                        data: {
                            folderId,
                            texts,
                            sourceLanguageCode: sourceLanguage,
                            targetLanguageCode: targetLanguage,
                            format: 'HTML',
                        },
                    }),
                );

                return data.translations.map(({text}) => text).forEach(resolve);
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } catch (error: any) {
                if (error instanceof AxiosError) {
                    const {response} = error;

                    if (response) {
                        const {status, statusText, data} = response as AxiosResponse;

                        switch (true) {
                            case LimitExceed.is(data.message):
                                throw new LimitExceed(data.message);
                            case AuthError.is(data.message):
                                throw new AuthError(data.message);
                            default:
                                throw new RequestError(status, statusText, data);
                        }
                    } else {
                        // eslint-disable-next-line no-console
                        console.error(error.code);
                        // eslint-disable-next-line no-console
                        console.error(error.cause);
                    }
                }

                throw new RequestError(0, error.message, {fatal: true});
            }
        };
    };
}

function processor(params: TranslatorParams, translate: Translate) {
    const {input, output, sourceLanguage, targetLanguage, varsFor, code} = params;
    const inputRoot = resolve(input);
    const outputRoot = resolve(output);

    /** Returns the parts of the file the engine left untranslated, one line each. */
    return async function (path: string): Promise<string[]> {
        const ext = extname(path);
        if (!['.yaml', '.md'].includes(ext)) {
            return [];
        }

        const inputPath = join(inputRoot, path);
        const output = languageRepath({inputRoot, outputRoot, sourceLanguage, targetLanguage});
        const vars = varsFor(path);

        const content = new FileLoader(inputPath);

        await content.load();

        if (Object.keys(vars).length && content.isString) {
            content.set(applyConditions(content.data as string, vars, inputPath));
        }

        if (!content.data) {
            await content.dump(output);
            return [];
        }

        const {schemas, ajvOptions} = await resolveSchemas({content: content.data, path});
        const {units, skeleton, warnings} = extract(content.data, {
            compact: true,
            code,
            source: {
                language: sourceLanguage,
                locale: 'RU',
            },
            target: {
                language: targetLanguage,
                locale: 'US',
            },
            schemas,
            ajvOptions,
        });

        if (!units.length) {
            await content.dump(output);
            return warnings;
        }

        const parts = await translate(path, units);

        content.set(compose(skeleton, parts, {useSource: true, schemas, ajvOptions}));

        await content.dump(output);

        return warnings;
    };
}

function translator(request: Request, cache: Cache, logger: Logger, stat: TargetStat): Translate {
    return async function (path: string, texts: string[]) {
        const promises: Promise<string>[] = [];
        const requests: Promise<void>[] = [];
        let buffer: string[] = [];
        let bufferSize = 0;

        const release = () => {
            requests.push(backoff(request(buffer), stat));
            buffer = [];
            bufferSize = 0;
        };

        for (const text of texts) {
            stat.unitsTotal++;
            stat.sourceChars += text.length;

            if (text.length >= BYTES_LIMIT) {
                logger.warn(path, 'Skip document part for translation. Part is too big.');
                stat.oversized++;
                promises.push(Promise.resolve(text));
            } else {
                const defer = cache.get(text) || new Defer();
                promises.push(defer.promise);

                if (!cache.get(text)) {
                    if (bufferSize + text.length > BYTES_LIMIT) {
                        release();
                    }

                    buffer.push(text);
                    bufferSize += text.length;
                }

                cache.set(text, defer);
            }
        }

        if (bufferSize) {
            release();
        }

        await Promise.all(requests);

        return Promise.all(promises);
    };
}

function wait(interval: number) {
    const defer = new Defer<void>();
    setTimeout(() => defer.resolve(), interval);
    return defer.promise;
}

async function backoff(action: () => Promise<void>, stat: TargetStat): Promise<void> {
    let retry = 0;

    while (++retry < RETRY_LIMIT) {
        try {
            await action();
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            if (RequestError.canRetry(error)) {
                stat.retries++;
                await wait(Math.pow(2, retry) * 1000);
            } else {
                throw error;
            }
        }
    }
}
